<template>
  <v-container class="mt-4 ml-2" fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="shadowCard">
          <v-card-title>
            <span class="text-subtitle-1">Panel de productividad</span>
            <v-chip small class="ml-3" color="blue-grey lighten-4">
              {{ totalEmpleados }} empleado{{ totalEmpleados === 1 ? '' : 's' }}
            </v-chip>
            <v-chip
              v-if="semanaTexto"
              small
              class="ml-2"
              color="primary"
              outlined
            >
              <v-icon left x-small>mdi-calendar-week</v-icon>
              Semana {{ semanaTexto }}
            </v-chip>
            <v-spacer></v-spacer>
            <v-btn
              class="mr-2"
              small
              tile
              color="blue-grey"
              dark
              @click="abrirCatalogoUnidades"
            >
              <v-icon left small>mdi-format-list-bulleted</v-icon>
              Unidades
            </v-btn>
            <v-btn
              class="mr-2"
              small
              tile
              color="green"
              dark
              :loading="exportandoExcel"
              @click="exportarExcel"
            >
              <v-icon left small>mdi-microsoft-excel</v-icon>
              Excel
            </v-btn>
            <v-btn color="primary" dark small tile @click="initialize">
              <v-icon left small>mdi-refresh</v-icon>
              Actualizar
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-sheet class="mt-1 pa-3 rounded-lg" color="grey lighten-4" elevation="0">
              <v-row dense class="align-end">
                <v-col cols="12" sm="12" md="5" lg="4">
                  <v-autocomplete
                    :items="opcionesSemana"
                    v-model="semanaSeleccionada"
                    label="Semana"
                    filled
                    dense
                    hide-details
                    item-text="texto"
                    item-value="value"
                    single-line
                    :filter="filtrarSemana"
                    @change="initialize"
                  >
                    <template v-slot:item="{ item }">
                      <div>
                        <div class="body-2">Semana {{ item.numero }} · {{ item.rangoTexto }}</div>
                      </div>
                    </template>
                    <template v-slot:selection="{ item }">
                      <span>Semana {{ item.numero }} · {{ item.rangoTexto }}</span>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6" md="2" lg="2">
                  <v-select
                    :items="opcionesEscuela"
                    v-model="escuela"
                    label="Escuela"
                    filled
                    dense
                    hide-details
                    item-text="text"
                    item-value="value"
                    single-line
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6" md="2" lg="2">
                  <v-autocomplete
                    :items="puestos"
                    v-model="puesto_seleccionado"
                    label="Puesto"
                    clearable
                    filled
                    dense
                    item-text="puesto"
                    item-value="idpuesto"
                    single-line
                    hide-details
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6" md="3" lg="2">
                  <v-autocomplete
                    label="Plantel"
                    filled
                    dense
                    hide-details
                    v-model="plantel"
                    :items="planteles"
                    item-text="plantel"
                    item-value="id_plantel"
                    clearable
                    single-line
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="2">
                  <v-text-field
                    label="Buscar"
                    filled
                    dense
                    hide-details
                    prepend-inner-icon="mdi-magnify"
                    v-model="search"
                    single-line
                    clearable
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="2" class="d-flex align-center pb-1">
                  <v-checkbox
                    v-model="soloConActual"
                    label="Con datos actuales"
                    dense
                    hide-details
                    class="mt-0 pt-0"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-sheet>

            <v-data-table
              class="elevation-0 mt-4"
              :headers="headers"
              :items="trabajadoresFiltrados"
              :search="search"
              :loading="cargar"
              loading-text="Cargando trabajadores..."
              dense
              item-key="id_trabajador"
              :mobile-breakpoint="100"
              :custom-sort="ordenarColumnas"
              :footer-props="{ 'items-per-page-options': [15, 30, 50, -1] }"
            >
              <template v-slot:item.meta="{ item }">
                <span>{{ item.meta || '—' }}</span>
              </template>
              <template v-slot:item.actual="{ item }">
                <v-chip
                  v-if="item.actual_origen === 'altas_semana'"
                  dark
                  color="primary"
                  class="px-3 actual-chip"
                  style="cursor: pointer"
                  @click="abrirDetalleAltas(item)"
                >
                  {{ item.actual }}
                </v-chip>
                <v-chip
                  v-else-if="item.actual_origen === 'ventas_semana'"
                  dark
                  color="primary"
                  class="px-3 actual-chip"
                  style="cursor: pointer"
                  @click="abrirDetalleVentas(item)"
                >
                  {{ item.actual }}
                </v-chip>
                <v-chip
                  v-else-if="item.actual_origen === 'tickets_semana'"
                  dark
                  color="primary"
                  class="px-3 actual-chip"
                  style="cursor: pointer"
                  @click="abrirDetalleTickets(item)"
                >
                  {{ item.actual }}
                </v-chip>
                <v-chip
                  v-else-if="item.actual_origen === 'tickets_resueltos_semana'"
                  dark
                  color="primary"
                  class="px-3 actual-chip"
                  style="cursor: pointer"
                  @click="abrirDetalleTicketsResueltos(item)"
                >
                  {{ item.actual }}
                </v-chip>
                <span v-else class="actual-valor">{{ item.actual || '—' }}</span>
              </template>
              <template v-slot:item.pct_meta="{ item }">
                <span>{{ item.pct_meta || '—' }}</span>
              </template>
              <template v-slot:item.unidad="{ item }">
                <span>{{ item.unidad || '—' }}</span>
              </template>
              <template v-slot:item.sueldo_depositado="{ item }">
                <div class="d-flex align-center justify-end" style="gap: 4px;">
                  <span>{{ item.sueldo_depositado || '—' }}</span>
                  <v-btn
                    v-if="item.sueldo_detalle"
                    icon
                    x-small
                    color="primary"
                    class="ml-1"
                    @click.stop="abrirDetalleSueldo(item)"
                  >
                    <v-icon x-small>mdi-information</v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-slot:item.valor_comercial_aportado="{ item }">
                <span>{{ item.valor_comercial_aportado || '—' }}</span>
              </template>
              <template v-slot:item.rol_colaborador="{ item }">
                <span>{{ item.rol_colaborador || '—' }}</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="dialogAltas" max-width="920px" scrollable>
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon left color="primary">mdi-account-plus</v-icon>
          Altas de la semana
          <span v-if="detalleAltasTitulo" class="ml-2">— {{ detalleAltasTitulo }}</span>
          <span v-if="altasSemana.inicio" class="text-caption grey--text ml-2">
            ({{ formatearFechaCorta(altasSemana.inicio) }} — {{ formatearFechaCorta(altasSemana.fin) }})
          </span>
          <v-spacer></v-spacer>
          <v-chip small dark color="primary" class="mr-2">{{ (altasDetalleItems || []).length }}</v-chip>
          <v-btn fab small text @click="dialogAltas = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-data-table
            :headers="headersAltas"
            :items="altasDetalleItems"
            dense
            hide-default-footer
            disable-pagination
            item-key="id_trabajador"
            class="elevation-0"
            :loading="cargandoAltas"
            loading-text="Cargando altas..."
          >
            <template v-slot:item.activo="{ item }">
              <v-chip
                x-small
                dark
                :color="item.activo_sn == 1 || item.activo_sn === '1' ? 'green' : 'grey'"
              >
                {{ item.activo || '—' }}
              </v-chip>
            </template>
            <template v-slot:no-data>
              <div class="py-4 text-center grey--text">No hay altas en esta semana</div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogVentas" max-width="980px" scrollable>
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon left color="primary">mdi-cash-register</v-icon>
          Ventas de la semana
          <span v-if="detalleVentasTitulo" class="ml-2">— {{ detalleVentasTitulo }}</span>
          <span v-if="ventasSemana.inicio" class="text-caption grey--text ml-2">
            ({{ formatearFechaCorta(ventasSemana.inicio) }} — {{ formatearFechaCorta(ventasSemana.fin) }})
          </span>
          <v-spacer></v-spacer>
          <v-chip small dark color="primary" class="mr-2">{{ (ventasDetalleItems || []).length }}</v-chip>
          <v-btn fab small text @click="dialogVentas = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-data-table
            :headers="headersVentas"
            :items="ventasDetalleItems"
            dense
            hide-default-footer
            disable-pagination
            item-key="id_alumno"
            class="elevation-0"
            :loading="cargandoVentas"
            loading-text="Cargando ventas..."
          >
            <template v-slot:item.unidad_negocio="{ item }">
              <v-chip
                x-small
                dark
                :color="item.unidad_negocio === 'FAST' ? 'blue' : 'indigo'"
              >
                {{ item.unidad_negocio || '—' }}
              </v-chip>
            </template>
            <template v-slot:item.pagado="{ item }">
              <span>{{ formatearMonto(item.pagado) }}</span>
            </template>
            <template v-slot:no-data>
              <div class="py-4 text-center grey--text">No hay ventas en esta semana para esta vendedora</div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogTickets" max-width="980px" scrollable>
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon left color="primary">mdi-ticket-confirmation</v-icon>
          Tickets respondidos
          <span v-if="detalleTicketsTitulo" class="ml-2">— {{ detalleTicketsTitulo }}</span>
          <span v-if="ticketsSemana.inicio" class="text-caption grey--text ml-2">
            ({{ formatearFechaCorta(ticketsSemana.inicio) }} — {{ formatearFechaCorta(ticketsSemana.fin) }})
          </span>
          <v-spacer></v-spacer>
          <v-chip small dark color="primary" class="mr-2">{{ (ticketsDetalleItems || []).length }}</v-chip>
          <v-btn fab small text @click="dialogTickets = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-data-table
            :headers="headersTickets"
            :items="ticketsDetalleItems"
            dense
            hide-default-footer
            disable-pagination
            item-key="idticket"
            class="elevation-0"
            :loading="cargandoTickets"
            loading-text="Cargando tickets..."
          >
            <template v-slot:no-data>
              <div class="py-4 text-center grey--text">No hay tickets respondidos en esta semana</div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogTicketsResueltos" max-width="520px">
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon left color="primary">mdi-checkbox-marked-circle-outline</v-icon>
          Tickets resueltos
          <span v-if="detalleTicketsResueltosTitulo" class="ml-2">— {{ detalleTicketsResueltosTitulo }}</span>
          <v-spacer></v-spacer>
          <v-btn fab small text @click="dialogTicketsResueltos = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="4" class="text-center">
              <div class="text-caption grey--text">Asignados</div>
              <div class="text-h6">{{ ticketsResueltosDetalle.asignados }}</div>
            </v-col>
            <v-col cols="4" class="text-center">
              <div class="text-caption grey--text">Cerrados</div>
              <div class="text-h6">{{ ticketsResueltosDetalle.cerrados }}</div>
            </v-col>
            <v-col cols="4" class="text-center">
              <div class="text-caption grey--text">% Resueltos</div>
              <div class="text-h6 primary--text">{{ ticketsResueltosDetalle.actual }}</div>
            </v-col>
          </v-row>
          <div class="text-caption grey--text text-center mt-3">
            Meta: 100% de tickets resueltos (cerrados / asignados) en la semana
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogSueldoDetalle" max-width="480px">
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon left color="primary">mdi-information</v-icon>
          Desglose sueldo depositado
          <span v-if="sueldoDetalleTitulo" class="ml-2">— {{ sueldoDetalleTitulo }}</span>
          <v-spacer></v-spacer>
          <v-btn fab small text @click="dialogSueldoDetalle = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4" v-if="sueldoDetalle">
          <v-simple-table dense>
            <tbody>
              <tr>
                <td class="grey--text">Monto total nómina</td>
                <td class="text-right font-weight-medium">{{ sueldoDetalle.monto_total_fmt }}</td>
              </tr>
              <tr>
                <td class="grey--text">(-) Comisiones semana actual</td>
                <td class="text-right">{{ sueldoDetalle.comisiones_semana_fmt }}</td>
              </tr>
              <tr>
                <td class="grey--text">= Sueldo sin comisiones actual</td>
                <td class="text-right">{{ sueldoDetalle.monto_sin_comisiones_fmt }}</td>
              </tr>
              <tr>
                <td class="grey--text">(+) Comisiones semana anterior</td>
                <td class="text-right">{{ sueldoDetalle.comisiones_semana_anterior_fmt }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Sueldo depositado</td>
                <td class="text-right font-weight-bold primary--text">{{ sueldoDetalle.sueldo_depositado }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="text-caption grey--text mt-3">
            Columna = sueldo sin comisiones de esta semana + comisiones de la semana anterior.
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogUnidades" max-width="980px" scrollable>
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon left color="blue-grey">mdi-format-list-bulleted</v-icon>
          Catálogo de unidades por puesto
          <v-spacer></v-spacer>
          <v-btn fab small text @click="dialogUnidades = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-row dense class="mb-2 align-end">
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="nuevaUnidadForm.id_puesto"
                :items="puestos"
                item-text="puesto"
                item-value="idpuesto"
                label="Puesto"
                filled
                dense
                hide-details
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="nuevaUnidadForm.id_plantel"
                :items="planteles"
                item-text="plantel"
                item-value="id_plantel"
                label="Sucursal"
                filled
                dense
                hide-details
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field
                v-model="nuevaUnidadForm.unidad"
                label="Unidad"
                filled
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field
                v-model="nuevaUnidadForm.meta_semana"
                label="Meta por semana"
                type="number"
                filled
                dense
                hide-details
                @keyup.enter="guardarNuevaUnidad"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="2">
              <v-btn
                color="primary"
                small
                block
                depressed
                :loading="guardandoUnidad"
                @click="guardarNuevaUnidad"
              >
                Agregar
              </v-btn>
            </v-col>
          </v-row>

          <v-data-table
            :headers="headersUnidades"
            :items="unidades"
            dense
            hide-default-footer
            disable-pagination
            item-key="id"
            class="elevation-0"
          >
            <template v-slot:item.id_puesto="{ item }">
              <v-autocomplete
                v-model="item.id_puesto"
                :items="puestos"
                item-text="puesto"
                item-value="idpuesto"
                dense
                filled
                hide-details
                @change="actualizarUnidad(item)"
              ></v-autocomplete>
            </template>
            <template v-slot:item.id_plantel="{ item }">
              <v-autocomplete
                v-model="item.id_plantel"
                :items="planteles"
                item-text="plantel"
                item-value="id_plantel"
                dense
                filled
                hide-details
                @change="actualizarUnidad(item)"
              ></v-autocomplete>
            </template>
            <template v-slot:item.unidad="{ item }">
              <v-text-field
                v-model="item.unidad"
                dense
                hide-details
                filled
                @blur="actualizarUnidad(item)"
              ></v-text-field>
            </template>
            <template v-slot:item.meta_semana="{ item }">
              <v-text-field
                v-model="item.meta_semana"
                dense
                hide-details
                filled
                type="number"
                @blur="actualizarUnidad(item)"
              ></v-text-field>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon small color="error" @click="eliminarUnidad(item)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
            <template v-slot:no-data>
              <div class="py-4 text-center grey--text">No hay unidades registradas</div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <carga v-if="cargar"></carga>
    <Alerta v-if="respuestaAlerta" :parametros="parametros"></Alerta>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import validarErrores from '@/mixins/validarErrores'
import funcionesExcel from '@/mixins/funcionesExcel'
import Alerta from '@/components/alertas/Alerta.vue'
import carga from '@/components/alertas/carga.vue'
var XLSX = require('xlsx')

export default {
  name: 'PanelProductividad',
  components: { Alerta, carga },
  mixins: [validarErrores, funcionesExcel],
  data: function () {
    return {
      puesto_seleccionado: null,
      plantel: null,
      escuela: null,
      semanaSeleccionada: null,
      opcionesSemana: [],
      planteles: [],
      puestos: [],
      opcionesEscuela: [
        { text: 'Todas', value: null },
        { text: 'INBI', value: 1 },
        { text: 'FAST', value: 2 },
      ],
      search: '',
      soloConActual: false,
      trabajadorERP: [],
      semanaCalculo: {
        inicio: null,
        fin: null,
      },
      unidades: [],
      dialogUnidades: false,
      dialogAltas: false,
      cargandoAltas: false,
      detalleAltasTitulo: '',
      altasDetalleItems: [],
      altasSemana: {
        total: 0,
        inicio: null,
        fin: null,
        items: [],
        por_reclutador: {},
      },
      dialogVentas: false,
      cargandoVentas: false,
      detalleVentasTitulo: '',
      ventasDetalleItems: [],
      ventasSemana: {
        total: 0,
        inicio: null,
        fin: null,
        items: [],
        por_trabajador: {},
      },
      dialogTickets: false,
      cargandoTickets: false,
      detalleTicketsTitulo: '',
      ticketsDetalleItems: [],
      ticketsSemana: {
        total: 0,
        inicio: null,
        fin: null,
        items: [],
        por_trabajador: {},
      },
      dialogTicketsResueltos: false,
      detalleTicketsResueltosTitulo: '',
      ticketsResueltosDetalle: {
        asignados: 0,
        cerrados: 0,
        actual: '—',
      },
      ticketsResueltosSemana: {
        inicio: null,
        fin: null,
        por_trabajador: {},
      },
      dialogSueldoDetalle: false,
      sueldoDetalleTitulo: '',
      sueldoDetalle: null,
      nuevaUnidadForm: {
        id_puesto: null,
        id_plantel: null,
        unidad: '',
        meta_semana: '',
      },
      guardandoUnidad: false,
      exportandoExcel: false,
      headersUnidades: [
        { text: 'Puesto', value: 'id_puesto' },
        { text: 'Sucursal', value: 'id_plantel' },
        { text: 'Unidad', value: 'unidad' },
        { text: 'Meta por semana', value: 'meta_semana', width: '150px' },
        { text: '', value: 'actions', sortable: false, width: '56px' },
      ],
      headersAltas: [
        { text: 'ID', value: 'id_trabajador', width: '80px' },
        { text: 'Nombre', value: 'nombre_completo' },
        { text: 'Puesto', value: 'puesto' },
        { text: 'Sucursal', value: 'plantel' },
        { text: 'Fecha alta', value: 'fecha_alta', width: '120px' },
        { text: 'Fecha ingreso', value: 'fecha_ingreso', width: '120px' },
        { text: 'Estatus', value: 'activo', width: '100px' },
      ],
      headersVentas: [
        { text: 'Matrícula', value: 'matricula', width: '100px' },
        { text: 'Alumno', value: 'nombre' },
        { text: 'Grupo', value: 'grupo' },
        { text: 'Sucursal', value: 'plantel' },
        { text: 'Escuela', value: 'unidad_negocio', width: '90px' },
        { text: 'Pagado', value: 'pagado', width: '110px' },
        { text: 'Fecha pago', value: 'fecha_pago', width: '120px' },
      ],
      headersTickets: [
        { text: 'Ticket', value: 'idticket', width: '90px' },
        { text: 'Tema', value: 'tema' },
        { text: 'Subtema', value: 'subtema' },
        { text: 'Sucursal', value: 'plantel' },
        { text: 'Fecha respuesta', value: 'fecha_respuesta', width: '130px' },
      ],
      headers: [
        { text: 'ID', value: 'id_trabajador' },
        { text: 'Trabajador', value: 'nombre_completo' },
        { text: 'Puesto', value: 'puesto' },
        { text: 'Depto', value: 'departamento' },
        { text: 'Sucursal', value: 'plantel' },
        { text: 'Meta', value: 'meta', width: '100px' },
        { text: 'Actual', value: 'actual', width: '110px' },
        { text: '% Meta', value: 'pct_meta', width: '100px' },
        { text: 'Unidad', value: 'unidad', width: '140px' },
        { text: 'Sueldo depositado', value: 'sueldo_depositado', width: '150px' },
        { text: 'Valor comercial aportado', value: 'valor_comercial_aportado', width: '180px' },
        { text: 'Rol del colaborador', value: 'rol_colaborador', width: '160px' },
      ],
    }
  },
  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP']),
    trabajadoresFiltrados: function () {
      var lista = this.trabajadorERP || []
      if (!this.soloConActual) return lista
      return lista.filter(function (item) {
        if (!item) return false
        var actual = item.actual
        if (actual == null) return false
        var s = String(actual).trim()
        return s !== '' && s !== '—' && s !== '-'
      })
    },
    totalEmpleados: function () {
      return (this.trabajadoresFiltrados || []).length
    },
    semanaTexto: function () {
      var ini = this.formatearFechaCorta(this.semanaCalculo && this.semanaCalculo.inicio)
      var fin = this.formatearFechaCorta(this.semanaCalculo && this.semanaCalculo.fin)
      if (!ini || ini === '—' || !fin || fin === '—') return ''
      return ini + ' — ' + fin
    },
  },
  watch: {
    puesto_seleccionado: function () {
      this.initialize()
    },
    plantel: function () {
      this.initialize()
    },
    escuela: function () {
      this.initialize()
    },
  },
  created: function () {
    var self = this
    self.opcionesSemana = self.generarSemanasAnio()
    self.semanaSeleccionada = self.obtenerSabadoActual()
    self.cargar = true
    Promise.all([self.getPuestos(), self.getPlanteles(), self.initialize()])
      .catch(function () {})
      .finally(function () {
        self.cargar = false
      })
  },
  methods: {
    aplicarUnidades: function (lista) {
      this.unidades = Array.isArray(lista) ? lista : []
    },
    pad2: function (n) {
      return String(n).padStart(2, '0')
    },
    fechaToSql: function (date) {
      if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return null
      return (
        date.getFullYear() +
        '-' +
        this.pad2(date.getMonth() + 1) +
        '-' +
        this.pad2(date.getDate())
      )
    },
    fechaToTexto: function (date) {
      if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return ''
      return (
        this.pad2(date.getDate()) +
        '/' +
        this.pad2(date.getMonth() + 1) +
        '/' +
        date.getFullYear()
      )
    },
    obtenerSabadoDe: function (date) {
      var d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      var day = d.getDay() // 0=dom … 6=sáb
      // días desde el sábado: sáb=0, dom=1, lun=2 … vie=6
      var diff = (day + 1) % 7
      d.setDate(d.getDate() - diff)
      d.setHours(0, 0, 0, 0)
      return d
    },
    obtenerSabadoActual: function () {
      return this.fechaToSql(this.obtenerSabadoDe(new Date()))
    },
    /** Número de semana de nómina (sáb–vie) dentro del año del sábado de inicio. */
    numeroSemanaNomina: function (sabado) {
      var anio = sabado.getFullYear()
      // Si el sábado cae en dic del año anterior (semana que cruza año), usar año del viernes
      var viernes = new Date(sabado)
      viernes.setDate(sabado.getDate() + 6)
      if (sabado.getFullYear() < viernes.getFullYear()) {
        anio = viernes.getFullYear()
      }
      var primerDia = new Date(anio, 0, 1)
      var primerSabado = this.obtenerSabadoDe(primerDia)
      // Si el primer sábado es del año anterior, la semana 1 es esa (cruza a ene)
      if (primerSabado.getFullYear() < anio) {
        // ok, cuenta desde ese sábado
      } else if (primerSabado.getTime() > primerDia.getTime()) {
        // 1 ene es dom–vie: el sábado de esa semana es del año anterior
        primerSabado = this.obtenerSabadoDe(primerDia)
      }
      var ms = sabado.getTime() - primerSabado.getTime()
      var num = Math.floor(ms / (7 * 24 * 60 * 60 * 1000)) + 1
      return num > 0 ? num : 1
    },
    generarSemanasAnio: function () {
      var anio = new Date().getFullYear()
      var sabado = this.obtenerSabadoDe(new Date(anio, 0, 1))
      var semanas = []
      for (var i = 0; i < 60; i++) {
        var viernes = new Date(sabado)
        viernes.setDate(sabado.getDate() + 6)
        if (sabado.getFullYear() > anio && viernes.getFullYear() > anio) break
        if (viernes.getFullYear() >= anio && sabado.getFullYear() <= anio) {
          var value = this.fechaToSql(sabado)
          var numero = this.numeroSemanaNomina(sabado)
          var rangoTexto = this.fechaToTexto(sabado) + ' — ' + this.fechaToTexto(viernes)
          semanas.push({
            value: value,
            inicio: value,
            fin: this.fechaToSql(viernes),
            numero: numero,
            rangoTexto: rangoTexto,
            texto: 'Semana ' + numero + ' · ' + rangoTexto,
          })
        }
        sabado.setDate(sabado.getDate() + 7)
      }
      return semanas
    },
    filtrarSemana: function (item, queryText) {
      var q = (queryText || '').toLowerCase()
      if (!q) return true
      return String(item.texto || '')
        .toLowerCase()
        .indexOf(q) !== -1
    },
    rangoSemanaSeleccionada: function () {
      var self = this
      var value = self.semanaSeleccionada
      var found = (self.opcionesSemana || []).find(function (s) {
        return s.value === value
      })
      if (found) {
        return { inicio: found.inicio, fin: found.fin }
      }
      if (value) {
        var partes = String(value).split('-')
        if (partes.length === 3) {
          var sabado = new Date(Number(partes[0]), Number(partes[1]) - 1, Number(partes[2]))
          var viernes = new Date(sabado)
          viernes.setDate(sabado.getDate() + 6)
          return { inicio: self.fechaToSql(sabado), fin: self.fechaToSql(viernes) }
        }
      }
      return { inicio: null, fin: null }
    },
    parseNumeroMeta: function (valor) {
      if (valor == null || valor === '' || valor === '—') return null
      var n = Number(
        String(valor)
          .replace(/[,$%\s]/g, '')
          .replace(/—/g, '')
      )
      return isFinite(n) ? n : null
    },
    valorParaOrdenar: function (item, campo) {
      if (!item) return null
      if (campo === 'id_trabajador') {
        var id = Number(item.id_trabajador)
        return isFinite(id) ? id : null
      }
      if (
        campo === 'meta' ||
        campo === 'actual' ||
        campo === 'pct_meta' ||
        campo === 'sueldo_depositado' ||
        campo === 'valor_comercial_aportado' ||
        campo === 'rol_colaborador'
      ) {
        return this.parseNumeroMeta(item[campo])
      }
      var texto = item[campo]
      if (texto == null || texto === '' || texto === '—') return ''
      return String(texto).toLowerCase()
    },
    ordenarColumnas: function (items, sortBy, sortDesc) {
      var self = this
      var lista = (items || []).slice()
      var campos = Array.isArray(sortBy) ? sortBy : sortBy ? [sortBy] : []
      var descs = Array.isArray(sortDesc) ? sortDesc : [!!sortDesc]
      if (!campos.length) return lista

      lista.sort(function (a, b) {
        for (var i = 0; i < campos.length; i++) {
          var campo = campos[i]
          var va = self.valorParaOrdenar(a, campo)
          var vb = self.valorParaOrdenar(b, campo)
          var cmp = 0

          if (va == null && vb == null) cmp = 0
          else if (va == null) cmp = 1
          else if (vb == null) cmp = -1
          else if (typeof va === 'number' && typeof vb === 'number') {
            cmp = va < vb ? -1 : va > vb ? 1 : 0
          } else {
            cmp = String(va).localeCompare(String(vb), 'es', { sensitivity: 'base' })
          }

          if (cmp !== 0) {
            return descs[i] ? -cmp : cmp
          }
        }
        return 0
      })
      return lista
    },
    calcularPctMeta: function (item) {
      if (!item) return '—'
      var meta = this.parseNumeroMeta(item.meta)
      var actual = this.parseNumeroMeta(item.actual)
      if (meta == null || meta === 0 || actual == null) return '—'
      var pct = (actual / meta) * 100
      if (!isFinite(pct)) return '—'
      return Math.round(pct) + '%'
    },
    formatearFechaCorta: function (valor) {
      if (!valor) return '—'
      var s = String(valor)
      if (s.length >= 10) return s.substring(0, 10)
      return s
    },
    formatearMonto: function (valor) {
      if (valor == null || valor === '' || valor === '—') return '—'
      var n = Number(String(valor).replace(/[,$%\s]/g, '').replace(/—/g, ''))
      if (!isFinite(n)) return '—'
      return (
        '$' +
        n.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      )
    },
    abrirDetalleAltas: function (item) {
      var self = this
      self.detalleAltasTitulo = item && item.nombre_completo ? item.nombre_completo : ''
      self.dialogAltas = true

      var aplicarDetalle = function (altas) {
        self.altasSemana = altas || {
          total: 0,
          inicio: null,
          fin: null,
          items: [],
          por_reclutador: {},
        }
        var idT = item && item.id_trabajador != null ? String(item.id_trabajador) : null
        var porR = (self.altasSemana.por_reclutador || {})[idT]
        self.altasDetalleItems = porR && porR.items ? porR.items : []
      }

      var tieneDatos =
        self.altasSemana &&
        ((self.altasSemana.items && self.altasSemana.items.length) ||
          (self.altasSemana.por_reclutador &&
            Object.keys(self.altasSemana.por_reclutador).length > 0))

      if (tieneDatos) {
        aplicarDetalle(self.altasSemana)
        return
      }

      self.cargandoAltas = true
      var rango = self.rangoSemanaSeleccionada()
      self.$http
        .get('panel.productividad.altas.semana', {
          params: { fecha_inicio: rango.inicio, fecha_fin: rango.fin },
        })
        .then(function (response) {
          aplicarDetalle(response.data)
        })
        .catch(function (error) {
          var msg = 'No se pudieron cargar las altas'
          if (error && error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message
          }
          self.validarError(msg)
        })
        .finally(function () {
          self.cargandoAltas = false
        })
    },
    abrirDetalleVentas: function (item) {
      var self = this
      self.detalleVentasTitulo = item && item.nombre_completo ? item.nombre_completo : ''
      self.dialogVentas = true

      var aplicarDetalle = function (ventas) {
        self.ventasSemana = ventas || {
          total: 0,
          inicio: null,
          fin: null,
          items: [],
          por_trabajador: {},
        }
        var idT = item && item.id_trabajador != null ? String(item.id_trabajador) : null
        var porT = (self.ventasSemana.por_trabajador || {})[idT]
        self.ventasDetalleItems = porT && porT.items ? porT.items : []
      }

      var tieneDatos =
        self.ventasSemana &&
        self.ventasSemana.por_trabajador &&
        Object.keys(self.ventasSemana.por_trabajador).length > 0

      if (tieneDatos) {
        aplicarDetalle(self.ventasSemana)
        return
      }

      self.cargandoVentas = true
      var rango = self.rangoSemanaSeleccionada()
      self.$http
        .get('panel.productividad.ventas.semana', {
          params: { fecha_inicio: rango.inicio, fecha_fin: rango.fin },
        })
        .then(function (response) {
          aplicarDetalle(response.data)
        })
        .catch(function (error) {
          var msg = 'No se pudieron cargar las ventas'
          if (error && error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message
          }
          self.validarError(msg)
        })
        .finally(function () {
          self.cargandoVentas = false
        })
    },
    abrirDetalleTickets: function (item) {
      var self = this
      self.detalleTicketsTitulo = item && item.nombre_completo ? item.nombre_completo : ''
      self.dialogTickets = true

      var aplicarDetalle = function (tickets) {
        self.ticketsSemana = tickets || {
          total: 0,
          inicio: null,
          fin: null,
          items: [],
          por_trabajador: {},
        }
        var idT = item && item.id_trabajador != null ? String(item.id_trabajador) : null
        var porT = (self.ticketsSemana.por_trabajador || {})[idT]
        self.ticketsDetalleItems = porT && porT.items ? porT.items : []
      }

      var tieneDatos =
        self.ticketsSemana &&
        self.ticketsSemana.por_trabajador &&
        Object.keys(self.ticketsSemana.por_trabajador).length > 0

      if (tieneDatos) {
        aplicarDetalle(self.ticketsSemana)
        return
      }

      self.cargandoTickets = true
      var rango = self.rangoSemanaSeleccionada()
      self.$http
        .get('panel.productividad.tickets.semana', {
          params: { fecha_inicio: rango.inicio, fecha_fin: rango.fin },
        })
        .then(function (response) {
          aplicarDetalle(response.data)
        })
        .catch(function (error) {
          var msg = 'No se pudieron cargar los tickets'
          if (error && error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message
          }
          self.validarError(msg)
        })
        .finally(function () {
          self.cargandoTickets = false
        })
    },
    abrirDetalleTicketsResueltos: function (item) {
      var self = this
      self.detalleTicketsResueltosTitulo =
        item && item.nombre_completo ? item.nombre_completo : ''
      self.dialogTicketsResueltos = true
      var det = (item && item.actual_detalle) || {}
      self.ticketsResueltosDetalle = {
        asignados: det.asignados != null ? det.asignados : 0,
        cerrados: det.cerrados != null ? det.cerrados : 0,
        actual: item && item.actual != null ? item.actual : '—',
      }
    },
    formatearMonedaLocal: function (valor) {
      var n = Number(valor)
      if (!isFinite(n)) n = 0
      return (
        '$' +
        n.toLocaleString('es-MX', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      )
    },
    abrirDetalleSueldo: function (item) {
      var det = (item && item.sueldo_detalle) || null
      if (!det) return
      var depositadoNum = Number(det.sueldo_depositado_num)
      if (!isFinite(depositadoNum)) {
        depositadoNum = Number(
          String(item.sueldo_depositado || '')
            .replace(/[,$%\s]/g, '')
            .replace(/—/g, '')
        )
      }
      this.sueldoDetalleTitulo = item && item.nombre_completo ? item.nombre_completo : ''
      this.sueldoDetalle = {
        monto_total_fmt: det.monto_total_fmt || this.formatearMonedaLocal(det.monto_total),
        comisiones_semana_fmt:
          det.comisiones_semana_fmt || this.formatearMonedaLocal(det.comisiones_semana),
        comisiones_semana_anterior_fmt:
          det.comisiones_semana_anterior_fmt ||
          this.formatearMonedaLocal(det.comisiones_semana_anterior),
        monto_sin_comisiones_fmt:
          det.monto_sin_comisiones_fmt ||
          this.formatearMonedaLocal(det.monto_sin_comisiones),
        sueldo_depositado: item.sueldo_depositado || this.formatearMonedaLocal(depositadoNum),
      }
      this.dialogSueldoDetalle = true
    },
    // ((valor económico generado - costo total del puesto) / costo total del puesto) * 100
    calcularRolColaborador: function (item) {
      if (!item) return '—'
      var valor = Number(
        String(item.valor_comercial_aportado == null ? '' : item.valor_comercial_aportado)
          .replace(/[,$%\s]/g, '')
          .replace(/—/g, '')
      )
      var costo = Number(
        String(item.sueldo_depositado == null ? '' : item.sueldo_depositado)
          .replace(/[,$%\s]/g, '')
          .replace(/—/g, '')
      )
      if (!isFinite(valor) || !isFinite(costo) || costo === 0) return '—'
      var pct = ((valor - costo) / costo) * 100
      if (!isFinite(pct)) return '—'
      return pct.toFixed(2) + '%'
    },
    initialize: function () {
      var self = this
      self.cargar = true
      var rango = self.rangoSemanaSeleccionada()
      var payload = {
        puesto_seleccionado: self.puesto_seleccionado,
        plantel: self.plantel,
        escuela: self.escuela,
        fecha_inicio: rango.inicio,
        fecha_fin: rango.fin,
      }
      return self.$http
        .post('panel.productividad.list', payload)
        .then(function (response) {
          var data = response.data || {}
          var rows = Array.isArray(data) ? data : data.trabajadores || []
          var unidades = Array.isArray(data) ? [] : data.unidades || []
          if (unidades.length || !Array.isArray(data)) {
            self.aplicarUnidades(unidades)
          }
          if (!Array.isArray(data) && data.altas_semana) {
            self.altasSemana = data.altas_semana
          }
          if (!Array.isArray(data) && data.ventas_semana) {
            self.ventasSemana = data.ventas_semana
          }
          if (!Array.isArray(data) && data.tickets_semana) {
            self.ticketsSemana = data.tickets_semana
          }
          if (!Array.isArray(data) && data.tickets_resueltos_semana) {
            self.ticketsResueltosSemana = data.tickets_resueltos_semana
          }
          if (!Array.isArray(data) && data.semana) {
            self.semanaCalculo = {
              inicio: data.semana.inicio || null,
              fin: data.semana.fin || null,
            }
          } else {
            var fallback =
              (!Array.isArray(data) &&
                (data.altas_semana ||
                  data.ventas_semana ||
                  data.tickets_semana ||
                  data.tickets_resueltos_semana)) ||
              {}
            self.semanaCalculo = {
              inicio: fallback.inicio || null,
              fin: fallback.fin || null,
            }
          }
          for (var i = 0; i < rows.length; i++) {
            rows[i].meta = rows[i].meta || '—'
            rows[i].unidad = rows[i].unidad || '—'
            rows[i].actual = rows[i].actual != null && rows[i].actual !== '' ? rows[i].actual : '—'
            rows[i].sueldo_depositado = rows[i].sueldo_depositado || '—'
            rows[i].valor_comercial_aportado = rows[i].valor_comercial_aportado || '—'
            rows[i].rol_colaborador = self.calcularRolColaborador(rows[i])
            rows[i].pct_meta = self.calcularPctMeta(rows[i])
          }
          self.trabajadorERP = rows
        })
        .catch(function (error) {
          var msg = 'No se pudieron cargar los trabajadores'
          if (error && error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message
          }
          self.validarError(msg)
        })
        .finally(function () {
          self.cargar = false
        })
    },
    cargarUnidades: function () {
      var self = this
      return self.$http
        .get('panel.productividad.unidades')
        .then(function (response) {
          self.aplicarUnidades(response.data || [])
        })
        .catch(function () {})
    },
    abrirCatalogoUnidades: function () {
      this.nuevaUnidadForm = {
        id_puesto: null,
        id_plantel: null,
        unidad: '',
        meta_semana: '',
      }
      this.dialogUnidades = true
      this.cargarUnidades()
    },
    guardarNuevaUnidad: function () {
      var self = this
      var idPuesto = self.nuevaUnidadForm.id_puesto
      var idPlantel = self.nuevaUnidadForm.id_plantel
      var unidad = (self.nuevaUnidadForm.unidad || '').trim()
      var metaSemana = (self.nuevaUnidadForm.meta_semana || '').toString().trim()
      if (!idPuesto) return self.validarError('Selecciona un puesto')
      if (!idPlantel) return self.validarError('Selecciona una sucursal')
      if (!unidad) return self.validarError('Escribe la unidad')
      if (!metaSemana) return self.validarError('Escribe la meta por semana')
      self.guardandoUnidad = true
      self.$http
        .post('panel.productividad.unidades.add', {
          id_puesto: idPuesto,
          id_plantel: idPlantel,
          unidad: unidad,
          meta_semana: metaSemana,
        })
        .then(function (response) {
          self.aplicarUnidades(response.data && response.data.unidades)
          self.nuevaUnidadForm = {
            id_puesto: null,
            id_plantel: null,
            unidad: '',
            meta_semana: '',
          }
          self.initialize()
        })
        .catch(function (error) {
          var msg = 'No se pudo agregar la unidad'
          if (error && error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message
          }
          self.validarError(msg)
        })
        .finally(function () {
          self.guardandoUnidad = false
        })
    },
    actualizarUnidad: function (item) {
      var self = this
      if (!item || !item.id) return
      var idPuesto = item.id_puesto
      var idPlantel = item.id_plantel
      var unidad = (item.unidad || '').trim()
      var metaSemana = (item.meta_semana || '').toString().trim()
      if (!idPuesto) return self.validarError('El puesto es requerido')
      if (!idPlantel) return self.validarError('La sucursal es requerida')
      if (!unidad) return self.validarError('La unidad no puede quedar vacía')
      if (!metaSemana) return self.validarError('La meta por semana no puede quedar vacía')
      self.$http
        .post('panel.productividad.unidades.update', {
          id: item.id,
          id_puesto: idPuesto,
          id_plantel: idPlantel,
          unidad: unidad,
          meta_semana: metaSemana,
        })
        .then(function (response) {
          self.aplicarUnidades(response.data && response.data.unidades)
          self.initialize()
        })
        .catch(function (error) {
          var msg = 'No se pudo actualizar la unidad'
          if (error && error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message
          }
          self.validarError(msg)
          self.cargarUnidades()
        })
    },
    eliminarUnidad: function (item) {
      var self = this
      if (!item || !item.id) return
      self.$http
        .post('panel.productividad.unidades.delete', { id: item.id })
        .then(function (response) {
          self.aplicarUnidades(response.data && response.data.unidades)
          self.initialize()
        })
        .catch(function (error) {
          var msg = 'No se pudo eliminar la unidad'
          if (error && error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message
          }
          self.validarError(msg)
        })
    },
    exportarExcel: function () {
      var self = this
      self.exportandoExcel = true

      var cargarCatalogo = self.$http
        .get('panel.productividad.unidades')
        .then(function (response) {
          var lista = response.data || []
          self.aplicarUnidades(lista)
          return lista
        })

      var cargarPuestos = self.puestos && self.puestos.length
        ? Promise.resolve(self.puestos)
        : self.$http.get('puestos.all').then(function (response) {
            self.puestos = response.data || []
            return self.puestos
          })

      Promise.all([cargarPuestos, cargarCatalogo])
        .then(function (resultados) {
          var puestos = resultados[0] || []
          var unidades = resultados[1] || []
          var puestoById = {}
          var porDepto = {}
          var puestosConCatalogo = {}

          function nombreDepto(valor) {
            var d = valor == null ? '' : String(valor).trim()
            return d || 'SIN DEPARTAMENTO'
          }

          function asegurarDepto(depto) {
            if (!porDepto[depto]) porDepto[depto] = []
          }

          function slugDepto(depto) {
            return String(depto)
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace(/[^a-zA-Z0-9]+/g, '_')
              .replace(/^_+|_+$/g, '')
              .toUpperCase()
          }

          function hojaSegura(nombre) {
            var hoja = String(nombre || 'HOJA').substring(0, 31)
            if (!hoja) hoja = 'HOJA'
            return hoja
          }

          for (var i = 0; i < puestos.length; i++) {
            var p = puestos[i]
            var idP = p.idpuesto != null ? p.idpuesto : p.id_puesto
            if (idP == null) continue
            puestoById[String(idP)] = p
            asegurarDepto(nombreDepto(p.departamento))
          }

          for (var j = 0; j < unidades.length; j++) {
            var u = unidades[j]
            if (u.id_puesto == null) continue
            puestosConCatalogo[String(u.id_puesto)] = true
            var puestoCat = puestoById[String(u.id_puesto)]
            var deptoCat = nombreDepto(
              (puestoCat && puestoCat.departamento) || u.departamento
            )
            asegurarDepto(deptoCat)
            porDepto[deptoCat].push({
              Puesto: u.puesto || (puestoCat && puestoCat.puesto) || '',
              Unidad: u.unidad || '',
              Meta: u.meta_semana != null ? String(u.meta_semana) : '',
              Sucursal: u.plantel || '',
            })
          }

          for (var k = 0; k < puestos.length; k++) {
            var puesto = puestos[k]
            var idPuesto = puesto.idpuesto != null ? puesto.idpuesto : puesto.id_puesto
            if (idPuesto == null) continue
            if (puestosConCatalogo[String(idPuesto)]) continue
            var deptoPuesto = nombreDepto(puesto.departamento)
            asegurarDepto(deptoPuesto)
            porDepto[deptoPuesto].push({
              Puesto: puesto.puesto || '',
              Unidad: '',
              Meta: '',
              Sucursal: '',
            })
          }

          var deptos = Object.keys(porDepto).sort(function (a, b) {
            return String(a).localeCompare(String(b), 'es')
          })

          if (!deptos.length) {
            self.validarError('No hay puestos para exportar')
            return
          }

          // Un solo Excel: una hoja por departamento (nombre ≤ 31 chars)
          var workbook = XLSX.utils.book_new()
          var hojasUsadas = {}

          for (var d = 0; d < deptos.length; d++) {
            var nombre = deptos[d]
            var filas = porDepto[nombre].slice().sort(function (a, b) {
              var c1 = String(a.Puesto).localeCompare(String(b.Puesto), 'es')
              if (c1 !== 0) return c1
              return String(a.Sucursal).localeCompare(String(b.Sucursal), 'es')
            })
            var filasHoja = filas.map(function (f) {
              return {
                Puesto: f.Puesto || '',
                Unidad: f.Unidad || '',
                Meta: f.Meta || '',
                Sucursal: f.Sucursal || '',
              }
            })
            var hoja = hojaSegura(slugDepto(nombre) || 'DEPTO')
            // Evitar nombres de hoja duplicados tras truncar
            var base = hoja
            var n = 2
            while (hojasUsadas[hoja]) {
              var sufijo = '_' + n
              hoja = base.substring(0, 31 - sufijo.length) + sufijo
              n++
            }
            hojasUsadas[hoja] = true
            var sheet = XLSX.utils.json_to_sheet(filasHoja)
            XLSX.utils.book_append_sheet(workbook, sheet, hoja)
          }

          XLSX.writeFile(workbook, 'PANEL_PRODUCTIVIDAD_POR_DEPARTAMENTO.xls')

          self.validarSuccess(
            'Excel descargado con ' + deptos.length + ' hojas (una por departamento)'
          )
        })
        .catch(function (error) {
          var msg = 'No se pudo generar el Excel'
          if (error && error.message) msg = error.message
          if (error && error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message
          }
          self.validarError(msg)
        })
        .finally(function () {
          self.exportandoExcel = false
        })
    },
    getPuestos: function () {
      var self = this
      return self.$http.get('puestos.all').then(function (response) {
        self.puestos = response.data || []
      })
    },
    getPlanteles: function () {
      var self = this
      return self.$http.get('planteles.activos').then(function (response) {
        self.planteles = (response.data || []).filter(function (el) {
          return [1, 2, 3, 4, '1', '2', '3', '4'].indexOf(el.estado) !== -1 || el.id_plantel == 13
        })
      })
    },
  },
}
</script>

<style scoped>
.actual-chip {
  font-size: 1.15rem !important;
  font-weight: 700 !important;
  height: 36px !important;
}
.actual-valor {
  font-size: 1.15rem;
  font-weight: 700;
}
</style>
